/* ============================================================
   BOOST — pengurusan admin  (/admin/boost, dalam AdminPinGate)  · UI English
   ============================================================
   PLAN_BOOST.md §🔄 [2026-11-04]. Semua melalui callable `boost` (aksi admin*):
     · Customer access — suis `config/boost.open` (fail-closed). Pelanggan nampak menu
       + halaman /boost HANYA bila suis hidup DAN rules `config/boost` sudah dideploy.
     · MCN — nama, penerangan, status, email staff partner (pintu halaman /mcn).
     · Program setiap MCN — jenis (TikTok TAP / Shopee Bind / TikTok Bind), status
       (Open / Coming soon / Hidden), link group WhatsApp, auto approve.
     · All applications — semua MCN, carian, CSV, Reject/Approve (aksi partner; admin dibenarkan).
   ⛔ Tiada butang PADAM (padam = keputusan user berasingan). Sorok = status Hidden / Closed.
   ⛔ Tetapan Google Sheet setiap program = milik PARTNER (halaman /mcn); admin buka
      halaman itu melalui "Open partner view".
   ============================================================ */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, RefreshCw, Plus, ExternalLink, Rocket } from 'lucide-react';

import '../design/tokens.css';
import { PageHeader, Card, CardBody, Btn, Badge, Segmented, EmptyState } from '../design/ui';
import { panggilBoost, mesejRalat, JENIS, SUSUNAN_JENIS } from '../services/boostService';
import { SenaraiPemohon } from './BoostPartnerView';

const LABEL_PROG_STATUS = { buka: 'Open', segera: 'Coming soon', tutup: 'Hidden' };
const TONE_PROG_STATUS = { buka: 'ok', segera: 'warn', tutup: 'neutral' };

export default function BoostAdminView() {
    const [data, setData] = useState(null);
    const [ralat, setRalat] = useState('');
    const [tab, setTab] = useState('mcn');
    const [cari, setCari] = useState('');
    const [borangMcn, setBorangMcn] = useState(null);    // null | {} (baru) | mcn (sunting)
    const [borangProg, setBorangProg] = useState(null);  // null | { mcnId, program? }

    /* `.then`, bukan async/await: lint React (set-state-in-effect) hanya menerima setState dalam callback. */
    const muat = useCallback(() => panggilBoost('adminMuat').then(
        (h) => { setData(h); setRalat(''); },
        (e) => setRalat(mesejRalat(e)),
    ), []);
    useEffect(() => { muat(); }, [muat]);

    const ditapis = useMemo(() => {
        const s = data?.permohonan || [];
        const q = cari.trim().toLowerCase();
        if (!q) return s;
        return s.filter((m) => [m.profil?.nama, m.email, m.profil?.wa, m.profil?.tiktok1, m.profil?.shopee, m.mcnNama]
            .some((v) => String(v || '').toLowerCase().includes(q)));
    }, [data, cari]);

    const selepasSimpan = () => { setBorangMcn(null); setBorangProg(null); muat(); };

    let isi;
    if (ralat) {
        isi = <EmptyState title="Could not load Boost" action={<Btn variant="ghost" onClick={() => { setRalat(''); muat(); }}>Try again</Btn>}>{ralat}</EmptyState>;
    } else if (!data) {
        isi = <div className="s-dim" style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '24px 0' }}><Loader2 size={16} className="animate-spin" /> Loading…</div>;
    } else {
        isi = (
            <>
                <SuisPelanggan buka={data.buka} onBerubah={muat} />

                <div style={{ margin: '18px 0 16px' }}>
                    <Segmented value={tab} onChange={setTab} options={[
                        { value: 'mcn', label: `MCN and programs · ${data.mcn.length}` },
                        { value: 'mohon', label: `All applications · ${data.permohonan.length}` },
                    ]} />
                </div>

                {tab === 'mcn' ? (
                    <>
                        {borangMcn ? (
                            <BorangMcn awal={borangMcn} onBatal={() => setBorangMcn(null)} onSiap={selepasSimpan} />
                        ) : (
                            <div style={{ marginBottom: 14 }}>
                                <Btn icon={<Plus size={14} strokeWidth={2} />} onClick={() => setBorangMcn({})}>New MCN</Btn>
                            </div>
                        )}
                        {data.mcn.length === 0 && !borangMcn && (
                            <EmptyState title="No MCN yet">Add Gorilla Cat first, then its TikTok TAP and Shopee Bind programs.</EmptyState>
                        )}
                        <div style={{ display: 'grid', gap: 14 }}>
                            {data.mcn.map((m) => (
                                <KadMcn key={m.id} m={m}
                                    onSunting={() => setBorangMcn(m)}
                                    borangProg={borangProg?.mcnId === m.id ? borangProg : null}
                                    setBorangProg={setBorangProg} onSiap={selepasSimpan} />
                            ))}
                        </div>
                    </>
                ) : (
                    <>
                        <SenaraiPemohon senarai={ditapis} jumlah={data.permohonan.length} cari={cari} setCari={setCari}
                            autoApprove={false} tunjukProgram namaFail={`boost-applications-${new Date().toISOString().slice(0, 10)}.csv`}
                            onBerubah={muat} />
                        {data.terpotong && <p className="s-hint">Showing the newest {data.permohonan.length} applications only.</p>}
                    </>
                )}
            </>
        );
    }

    return (
        <div className="sesaat-m-tight" style={{ padding: '26px 28px 48px' }}>
            <PageHeader
                title="Boost"
                subtitle="MCN programs, partner accounts and customer applications."
                sisiTajuk={<Rocket size={18} strokeWidth={1.8} style={{ color: 'var(--s-ink-3)' }} />}
                suisBahasa={false}
                actions={<Btn variant="ghost" size="sm" icon={<RefreshCw size={14} strokeWidth={1.9} />} onClick={() => { setData(null); muat(); }}>Refresh</Btn>}
            />
            {isi}
        </div>
    );
}

/* ── Suis buka kepada pelanggan ─────────────────────────────── */
function SuisPelanggan({ buka, onBerubah }) {
    const [sahkan, setSahkan] = useState(false);
    const [sibuk, setSibuk] = useState(false);
    const [ralat, setRalat] = useState('');
    async function tetapkan(nilai) {
        setSibuk(true); setRalat('');
        try { await panggilBoost('adminBuka', { buka: nilai }); setSahkan(false); onBerubah(); } catch (e) { setRalat(mesejRalat(e)); }
        setSibuk(false);
    }
    return (
        <Card>
            <CardBody>
                <div className="s-togrow">
                    <div className="s-togrow__txt">
                        <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--s-ink)' }}>
                            Open Boost to customers {buka ? <Badge tone="ok">On</Badge> : <Badge>Off — admin only</Badge>}
                        </p>
                        <p className="s-dim" style={{ margin: '4px 0 0', lineHeight: 1.55 }}>
                            When on, the Boost menu and page appear for every logged-in customer (Premium, Fighter and SMART can apply; others see the upgrade screen).
                            Needs the Firestore rule for <code>config/boost</code> deployed first — without it, customers stay locked out.
                        </p>
                    </div>
                    <button type="button" role="switch" aria-checked={buka} aria-label="Open Boost to customers"
                        className={buka ? 's-switch s-switch--on' : 's-switch'} disabled={sibuk}
                        onClick={() => (buka ? tetapkan(false) : setSahkan(true))}>
                        <span className="s-switch__dot" />
                    </button>
                </div>
                {sahkan && (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--s-line)' }}>
                        <span className="s-dim">This shows Boost to about 1,700 customers right away. Continue?</span>
                        <Btn size="sm" onClick={() => tetapkan(true)} disabled={sibuk}>Yes, open Boost</Btn>
                        <Btn variant="ghost" size="sm" onClick={() => setSahkan(false)} disabled={sibuk}>Cancel</Btn>
                    </div>
                )}
                {ralat && <p role="alert" style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--s-stop)' }}>{ralat}</p>}
            </CardBody>
        </Card>
    );
}

/* ── Kad satu MCN + programnya ─────────────────────────────── */
function KadMcn({ m, onSunting, borangProg, setBorangProg, onSiap }) {
    const jenisBelum = SUSUNAN_JENIS.filter((j) => !m.program.some((p) => p.jenis === j));
    return (
        <Card>
            <CardBody>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--s-ink)' }}>{m.nama}</span>
                    <Badge tone={m.status === 'aktif' ? 'ok' : 'neutral'}>{m.status === 'aktif' ? 'Active' : 'Closed'}</Badge>
                    <span className="s-dim">id: {m.id}</span>
                    <div style={{ flex: 1 }} />
                    <Btn variant="ghost" size="sm" icon={<ExternalLink size={13} strokeWidth={1.9} />}
                        onClick={() => window.open(`/mcn?m=${encodeURIComponent(m.id)}`, '_blank', 'noopener')}>Open partner view</Btn>
                    <Btn variant="ghost" size="sm" onClick={onSunting}>Edit MCN</Btn>
                </div>
                {m.penerangan && <p className="s-dim" style={{ margin: '6px 0 0', lineHeight: 1.55 }}>{m.penerangan}</p>}
                <p className="s-dim" style={{ margin: '6px 0 0' }}>
                    Partner emails: {m.partnerEmails.length ? m.partnerEmails.join(', ') : <em>none yet — add the MCN staff email so they can open /mcn</em>}
                </p>

                <div className="s-tblwrap" style={{ marginTop: 14 }}>
                    <table className="s-table s-table--rapat">
                        <thead>
                            <tr><th>Program</th><th>Status</th><th>Approve</th><th>WhatsApp group</th><th>Google Sheet</th><th className="s-td-num">Applications</th><th /></tr>
                        </thead>
                        <tbody>
                            {m.program.length === 0 && <tr><td colSpan={7} className="s-dim">No programs yet.</td></tr>}
                            {m.program.map((p) => (
                                <tr key={p.id}>
                                    <td className="s-td-strong">{p.label}</td>
                                    <td><Badge tone={TONE_PROG_STATUS[p.status]}>{LABEL_PROG_STATUS[p.status] || p.status}</Badge></td>
                                    <td>{p.autoApprove ? 'Auto' : 'Manual'}</td>
                                    <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.waLink || <span className="s-dim">—</span>}</td>
                                    <td>{p.sheet.on ? <Badge tone={p.sheet.ujian?.ok === false ? 'stop' : 'ok'}>On</Badge> : <Badge>Off</Badge>}</td>
                                    <td className="s-td-num">{p.bil}</td>
                                    <td style={{ textAlign: 'right' }}><Btn variant="quiet" size="sm" onClick={() => setBorangProg({ mcnId: m.id, program: p })}>Edit</Btn></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {borangProg ? (
                    <BorangProgram mcnId={m.id} awal={borangProg.program} jenisBelum={jenisBelum}
                        onBatal={() => setBorangProg(null)} onSiap={onSiap} />
                ) : jenisBelum.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                        <Btn variant="ghost" size="sm" icon={<Plus size={13} strokeWidth={2} />} onClick={() => setBorangProg({ mcnId: m.id })}>Add program</Btn>
                    </div>
                )}
            </CardBody>
        </Card>
    );
}

/* ── Borang MCN (baru / sunting) ───────────────────────────── */
function BorangMcn({ awal, onBatal, onSiap }) {
    const [f, setF] = useState({
        nama: awal.nama || '', penerangan: awal.penerangan || '', status: awal.status || 'aktif',
        partnerEmails: (awal.partnerEmails || []).join('\n'),
    });
    const [sibuk, setSibuk] = useState(false);
    const [ralat, setRalat] = useState('');
    async function simpan(e) {
        e.preventDefault();
        setSibuk(true); setRalat('');
        try {
            await panggilBoost('adminMcnSimpan', { id: awal.id || '', ...f });
            onSiap();
        } catch (err) { setRalat(mesejRalat(err)); setSibuk(false); }
    }
    return (
        <Card style={{ marginBottom: 14 }}>
            <CardBody>
                <form onSubmit={simpan}>
                    <p style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: 'var(--s-ink)' }}>{awal.id ? `Edit ${awal.nama}` : 'New MCN'}</p>
                    <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
                        <div>
                            <label className="s-lbl" htmlFor="ba-nama">MCN name</label>
                            <input id="ba-nama" className="s-input" value={f.nama} onChange={(e) => setF({ ...f, nama: e.target.value })} placeholder="Gorilla Cat" maxLength={60} />
                            {!awal.id && <p className="s-hint">The id is made from the name and can't change later.</p>}
                        </div>
                        <div>
                            <label className="s-lbl" htmlFor="ba-status">Status</label>
                            <select id="ba-status" className="s-select" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
                                <option value="aktif">Active — customers see it</option>
                                <option value="tutup">Closed — hidden from customers</option>
                            </select>
                        </div>
                    </div>
                    <div style={{ marginTop: 12 }}>
                        <label className="s-lbl" htmlFor="ba-pen">Description (customers see this)</label>
                        <textarea id="ba-pen" className="s-input" rows={2} style={{ height: 'auto', paddingTop: 8 }} maxLength={300}
                            value={f.penerangan} onChange={(e) => setF({ ...f, penerangan: e.target.value })} />
                    </div>
                    <div style={{ marginTop: 12 }}>
                        <label className="s-lbl" htmlFor="ba-emel">Partner staff emails (one per line, up to 10)</label>
                        <textarea id="ba-emel" className="s-input" rows={3} style={{ height: 'auto', paddingTop: 8 }}
                            value={f.partnerEmails} onChange={(e) => setF({ ...f, partnerEmails: e.target.value })} placeholder="staff@gorillacat.com" />
                        <p className="s-hint">They sign in to SESAAT with this email and open app.sesaat.ai/mcn. They see this MCN's applicants only.</p>
                    </div>
                    {ralat && <p role="alert" style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--s-stop)' }}>{ralat}</p>}
                    <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                        <Btn type="submit" disabled={sibuk}>{sibuk ? 'Saving…' : 'Save MCN'}</Btn>
                        <Btn variant="ghost" onClick={onBatal} disabled={sibuk}>Cancel</Btn>
                    </div>
                </form>
            </CardBody>
        </Card>
    );
}

/* ── Borang program (baru / sunting) ───────────────────────── */
function BorangProgram({ mcnId, awal, jenisBelum, onBatal, onSiap }) {
    const [f, setF] = useState({
        jenis: awal?.jenis || jenisBelum[0] || 'tiktok_tap',
        status: awal?.status || 'segera',
        penerangan: awal?.penerangan || '', waLink: awal?.waLink || '',
        autoApprove: awal ? awal.autoApprove : true,
    });
    const [sibuk, setSibuk] = useState(false);
    const [ralat, setRalat] = useState('');
    async function simpan(e) {
        e.preventDefault();
        setSibuk(true); setRalat('');
        try {
            await panggilBoost('adminProgramSimpan', { mcnId, ...f });
            onSiap();
        } catch (err) { setRalat(mesejRalat(err)); setSibuk(false); }
    }
    return (
        <form onSubmit={simpan} style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--s-line)' }}>
            <p style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: 'var(--s-ink)' }}>{awal ? `Edit ${awal.label}` : 'Add program'}</p>
            <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                <div>
                    <label className="s-lbl" htmlFor="bp-jenis">Program type</label>
                    <select id="bp-jenis" className="s-select" value={f.jenis} disabled={!!awal} onChange={(e) => setF({ ...f, jenis: e.target.value })}>
                        {(awal ? [awal.jenis] : jenisBelum).map((j) => (
                            <option key={j} value={j}>{JENIS[j].label}{JENIS[j].bind ? ' — 1 MCN only' : ' — join many'}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="s-lbl" htmlFor="bp-status">Status</label>
                    <select id="bp-status" className="s-select" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
                        <option value="buka">Open — customers can apply</option>
                        <option value="segera">Coming soon — shown, no Apply button</option>
                        <option value="tutup">Hidden</option>
                    </select>
                </div>
            </div>
            <div style={{ marginTop: 12 }}>
                <label className="s-lbl" htmlFor="bp-pen">Short description (customers see this)</label>
                <input id="bp-pen" className="s-input" maxLength={300} value={f.penerangan} onChange={(e) => setF({ ...f, penerangan: e.target.value })}
                    placeholder={f.jenis === 'shopee_bind' ? 'Bind your Shopee account to Gorilla Cat' : 'Seller campaigns with bonus commission'} />
            </div>
            <div style={{ marginTop: 12 }}>
                <label className="s-lbl" htmlFor="bp-wa">WhatsApp group link (shown only after approval)</label>
                <input id="bp-wa" className="s-input" value={f.waLink} onChange={(e) => setF({ ...f, waLink: e.target.value })} placeholder="https://chat.whatsapp.com/…" />
            </div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, fontSize: 13, color: 'var(--s-ink-2)', cursor: 'pointer' }}>
                <input type="checkbox" checked={f.autoApprove} onChange={(e) => setF({ ...f, autoApprove: e.target.checked })} />
                Auto approve — customers get access as soon as they apply (Gorilla Cat: on)
            </label>
            {ralat && <p role="alert" style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--s-stop)' }}>{ralat}</p>}
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <Btn type="submit" disabled={sibuk}>{sibuk ? 'Saving…' : 'Save program'}</Btn>
                <Btn variant="ghost" onClick={onBatal} disabled={sibuk}>Cancel</Btn>
            </div>
        </form>
    );
}
